import { SyntheticEvent, useContext, useEffect, useRef, useState } from "react";

import { ArrowBack, ArrowForward } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Theme,
  useMediaQuery,
  useTheme,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import { BulletinResource, getBulletinById } from "@rubi/api-generated";
import { ConfirmDialog } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useDrawer } from "@/common/SlideoutDrawer/DrawerContext";
import { Access } from "@/components/alerts/access/Access";
import { DisplayAlert } from "@/components/alerts/add/DisplayAlert";
import ResolveBulletin from "@/components/alerts/add/DisplayAlert/ResolveBulletin";
import { ALERT_STATUS, BULLETIN_TYPE } from "@/components/alerts/constants";
import {
  AlertViewContext,
  SingleAlertContext,
} from "@/components/alerts/context";
import ShareMultipleDialog from "@/components/alerts/ShareMultipleDialog";
import { updateToContextStructure } from "@/components/alerts/utils";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { IEvidenceFile, IPdfFile, IUserPermission } from "@/types";

import { DisplayAlertTopButton } from "./DisplayAlertTopButton";
import "@/components/alerts/alerts.scss";

interface DisplayAlertWrapperProps {
  mode: ALERT_STATUS;
  display_type: "main" | "compare";
  queryNextPage?: () => void;
}

export const DisplayAlertWrapper = ({
  mode,
  display_type,
  queryNextPage = () => {},
}: DisplayAlertWrapperProps) => {
  const { currentItems } = useContext(AlertViewContext);
  const { currentUser } = useContext(AuthStateContext);
  const { alertProperties, setAlertProperties } =
    useContext(SingleAlertContext);
  const { setDrawerWidth, closeDrawer } = useDrawer();
  const { accessGuard, setScrollToBulletinId } = useContext(AlertViewContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("bulletin");
  const accessView = searchParams.get("access");
  const idToUse =
    display_type === "main"
      ? parseInt(searchParams.get("bulletinId") ?? "0")
      : parseInt(searchParams.get("compareBulletinId") ?? "0");
  const isAlertSingleView = location.pathname.includes("/single");
  const [alertPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );

  const [currentItem, setCurrentItem] = useState<BulletinResource>(
    {} as BulletinResource,
  );
  const alertCreatorId = currentItem?.creator_user_id;

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [isCreator, setIsCreator] = useState(
    currentUser?.id === alertCreatorId,
  );
  const [isLoaded, setIsLoaded] = useState(false);

  const readOnly = alertPermissions
    ? !alertPermissions.canCreateBulletin
    : false;
  const { resolutions } = alertProperties;
  const hasUnpublishedResolution = resolutions?.some(
    (resolution) =>
      !resolution.published &&
      (resolution.details?.length > 0 ||
        Object.values(resolution.attachments).length > 0 ||
        resolution.reason?.length > 0),
  );

  useEffect(() => {
    const searchParams = window.location.search;
    if (searchParams.includes("share=true")) {
      setIsShareModalOpen(true);
    }
  }, []);

  const navigate = useNavigate();

  const theme = useTheme();
  const isSmall = useMediaQuery((theme as Theme).breakpoints.down("sm"));
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedTab: "access" | "displayalert" =
    accessView === "true" ? "access" : "displayalert";

  const alertsOrDrafts = currentItem?.is_draft === 1 ? "drafts" : "alerts";

  // In an effect rather than in the render body: navigating while rendering updates the router
  // mid-render, which React warns about and which fires again on every render until the route
  // changes.
  useEffect(() => {
    const bulletinId = searchParams.get("bulletinId");
    if (isSmall && bulletinId != null && !isAlertSingleView) {
      navigate(`/rubialert/${alertsOrDrafts}/${bulletinId}/single`);
    }
  }, [isSmall, isAlertSingleView, alertsOrDrafts, searchParams, navigate]);

  const getAlertById = (id: number) => {
    getBulletinById({ path: { bulletin_id: id } })
      .then((res) => {
        setCurrentItem(res.data!);
        setIsLoaded(true);
      })
      .catch(() => {
        toast.error(
          "You do not have access to this bulletin, or it no longer exists.",
        );
        // The bulletin could not be loaded (e.g. no access / 404). Close the
        // failed view instead of hanging on the loading spinner.
        setSearchParams((params) => {
          if (display_type === "compare") {
            params.delete("compareBulletinId");
            params.delete("pinToFront");
          } else {
            params.delete("bulletinId");
            params.delete("compareBulletinId");
            params.delete("pinToFront");
            params.delete("access");
          }
          return params;
        });
        if (display_type === "main") {
          closeDrawer();
        }
      });
  };

  const shareModal = () => {
    if (!isShareModalOpen) return null;
    else
      return (
        <ShareMultipleDialog
          isShareModalOpenArg={isShareModalOpen}
          setIsShareModalOpenArg={setIsShareModalOpen}
          bulletinIDs={[currentItem?.id]}
        />
      );
  };

  const resolveCard = () => {
    if (isResolveOpen) {
      return (
        <ResolveBulletin
          bulletinType={currentItem?.type as BULLETIN_TYPE}
          bulletinId={currentItem?.id}
          setIsResolveOpen={setIsResolveOpen}
        />
      );
    }
  };

  const hasNextAlert = () => {
    if (currentItems) {
      const pinToFront = searchParams.get("pinToFront");
      const bulletinId = searchParams.get("bulletinId");
      const compareBulletinId = parseInt(
        searchParams.get("compareBulletinId") ?? "0",
      );
      let index = 0;
      if (compareBulletinId) {
        index = currentItems.map((item) => item.id).indexOf(compareBulletinId);
      } else {
        index = currentItems.map((item) => item.id).indexOf(idToUse);
      }
      index++;
      // if there's less than 5 alerts, load more page.
      if (index === currentItems.length - 5) {
        queryNextPage();
      }

      // In compare mode, skip over the same bulletin as the pinned main card.
      if (pinToFront === "true" && bulletinId) {
        while (
          index < currentItems.length &&
          currentItems[index].id.toString() === bulletinId
        ) {
          index++;
        }
      }

      return index < currentItems.length;
    }
    return false;
  };

  const hasPreviousAlert = () => {
    if (currentItems) {
      const pinToFront = searchParams.get("pinToFront");
      const bulletinId = searchParams.get("bulletinId");
      const compareBulletinId = parseInt(
        searchParams.get("compareBulletinId") ?? "0",
      );
      let index = 0;
      if (compareBulletinId) {
        index = currentItems.map((item) => item.id).indexOf(compareBulletinId);
      } else {
        index = currentItems.map((item) => item.id).indexOf(idToUse);
      }
      index--;

      // In compare mode, skip over the same bulletin as the pinned main card.
      if (pinToFront === "true" && bulletinId) {
        while (index >= 0 && currentItems[index].id.toString() === bulletinId) {
          index--;
        }
      }

      return index >= 0;
    }
    return false;
  };

  const onNextAlert = () => {
    const pinToFront = searchParams.get("pinToFront");
    const bulletinId = searchParams.get("bulletinId");
    if (currentItems) {
      const compareBulletinId = parseInt(
        searchParams.get("compareBulletinId") ?? "0",
      );
      let index = 0;
      if (compareBulletinId) {
        index = currentItems.map((item) => item.id).indexOf(compareBulletinId);
        index++;
      } else {
        index = currentItems.map((item) => item.id).indexOf(idToUse);
        index++;
      }
      if (index < currentItems.length) {
        const targetId = currentItems[index].id;
        if (pinToFront !== "true") {
          setSearchParams((searchParams) => {
            searchParams.set("bulletinId", targetId.toString());
            return searchParams;
          });
          setScrollToBulletinId(targetId);
        } else {
          while (
            index < currentItems.length &&
            bulletinId &&
            currentItems[index].id.toString() === bulletinId
          ) {
            index++;
          }

          if (index < currentItems.length) {
            const nextId = currentItems[index].id;
            setSearchParams((searchParams) => {
              searchParams.set("compareBulletinId", nextId.toString());
              setDrawerWidth(window.innerWidth * 0.7);
              return searchParams;
            });
            setScrollToBulletinId(nextId);
          }
        }
      }
    }
    // if this is the last alert, use queryNextPage
    if (!hasNextAlert()) {
      queryNextPage();
    }
  };

  const onPreviousAlert = () => {
    const pinToFront = searchParams.get("pinToFront");
    const bulletinId = searchParams.get("bulletinId");

    if (currentItems) {
      const compareBulletinId = parseInt(
        searchParams.get("compareBulletinId") ?? "0",
      );
      let index = 0;
      if (compareBulletinId) {
        index = currentItems.map((item) => item.id).indexOf(compareBulletinId);
        index--;
      } else {
        index = currentItems.map((item) => item.id).indexOf(idToUse);
        index--;
      }
      if (index >= 0) {
        const targetId = currentItems[index].id;
        if (pinToFront !== "true") {
          setSearchParams((searchParams) => {
            searchParams.set("bulletinId", targetId.toString());
            return searchParams;
          });
          setScrollToBulletinId(targetId);
        } else {
          while (
            index >= 0 &&
            bulletinId &&
            currentItems[index].id.toString() === bulletinId
          ) {
            index--;
          }

          if (index >= 0) {
            const prevId = currentItems[index].id;
            setSearchParams((searchParams) => {
              searchParams.set("compareBulletinId", prevId.toString());
              setDrawerWidth(window.innerWidth * 0.7);
              return searchParams;
            });
            setScrollToBulletinId(prevId);
          }
        }
      }
    }
  };

  // binding left and right keys to next and previous alerts only for type main
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isEditableTarget =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;

      if (isEditableTarget) return;

      if (e.key === "ArrowRight") {
        onNextAlert();
      } else if (e.key === "ArrowLeft") {
        onPreviousAlert();
      }
    };
    if (display_type === "main") {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [onNextAlert, onPreviousAlert, queryNextPage]);

  const PreviousButton = () => {
    return (
      <Button
        variant="outlined"
        color="primary"
        className="border-radius-rounded p-0"
        size="medium"
        onClick={onPreviousAlert}
        style={{
          minWidth: "48px",
          minHeight: "48px",
        }}
        disabled={isResolveOpen || !hasPreviousAlert()}
      >
        <ArrowBack />
      </Button>
    );
  };

  const NextButton = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        className="border-radius-rounded"
        onClick={onNextAlert}
        disabled={isResolveOpen || !hasNextAlert()}
        style={{
          minWidth: "48px",
          minHeight: "48px",
          padding: 0,
        }}
      >
        <ArrowForward />
      </Button>
    );
  };

  const desktopBottomButtons = () => {
    // if only one alert is selected, show the bottom buttons on main, otherwise show the bottom buttons on compare
    const compareBulletinId = searchParams.get("compareBulletinId");
    if (compareBulletinId && display_type === "main") {
      return null;
    }
    const showBottomButtoms = hasPreviousAlert() || hasNextAlert();
    if (!showBottomButtoms) return null;
    return (
      <div className="desktop-bottom-buttons d-flex mb-3 w-90 justify-content-end">
        <div className="mr-3">
          <PreviousButton />
        </div>
        <div>
          <NextButton />
        </div>
      </div>
    );
  };

  // useEffects
  useEffect(() => {
    const isCreator =
      currentUser?.id?.toString() === alertCreatorId?.toString();
    setIsCreator(isCreator);
  }, [currentUser?.id, alertCreatorId]);

  // on navigation to a new alert
  useEffect(() => {
    setIsResolveOpen(false);
    if (Object.keys(currentItem).length > 0) {
      const alertProperties = updateToContextStructure(currentItem);
      setAlertProperties(alertProperties);
    }
  }, [currentItem]);

  useEffect(() => {
    if (idToUse) getAlertById(idToUse);
  }, [idToUse]);

  // Reset scroll position when switching to a different bulletin. The wrapper
  // is reused across bulletin navigations (only the URL param changes), so the
  // surrounding scroll container otherwise inherits the previous bulletin's
  // scroll position — users would land mid-page on the new bulletin.
  useEffect(() => {
    let el: HTMLElement | null = containerRef.current;
    while (el) {
      const overflowY = window.getComputedStyle(el).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        el.scrollTop = 0;
        break;
      }
      el = el.parentElement;
    }
  }, [idToUse]);

  const handleTabChange = (
    _event: SyntheticEvent,
    newValue: "access" | "displayalert",
  ) => {
    setSearchParams((params) => {
      if (newValue === "access") {
        params.set("access", "true");
      } else {
        params.delete("access");
      }
      return params;
    });
  };

  if (idToUse === 0) return null;
  if (display_type === "compare") {
    const bulletinId = searchParams.get("bulletinId");
    if (bulletinId === idToUse.toString()) {
      return;
    }
  }

  if (!isLoaded)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );

  return (
    <>
      <div
        className="alert-container h-100 w-100 justify-content-between"
        ref={containerRef}
        style={{ position: "relative" }}
      >
        {/* Container for DisplayAlertTopButton's width query: the header
            collapses with the panel (drawer / compare split), not the viewport. */}
        <div className="w-100" style={{ containerType: "inline-size" }}>
          <DisplayAlertTopButton
            currentItem={currentItem}
            mode={mode}
            display_type={display_type}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            readOnly={readOnly}
            isCreator={isCreator}
            isResolveOpen={isResolveOpen}
            setIsResolveOpen={setIsResolveOpen}
            hasUnpublishedResolution={hasUnpublishedResolution}
            setDrawerWidth={setDrawerWidth}
          />
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={selectedTab} onChange={handleTabChange}>
              <Tab label={t("bulletin_details")} value="displayalert" />
              <Tab label={t("access_management")} value="access" />
            </Tabs>
          </Box>
          <div
            style={
              display_type === "compare"
                ? {
                    height: "max-content",
                  }
                : {}
            }
          >
            {isResolveOpen && resolveCard()}
            {selectedTab === "access" ? (
              <Access bulletinId={String(idToUse)} />
            ) : (
              <DisplayAlert
                mode="add"
                issuing_police_service={
                  currentItem.issuing_police_service?.name ?? ""
                }
                associatedCrimes={currentItem.associated_crime}
                occurrenceNumbers={currentItem.occurrence_number}
                issuedTime={currentItem.issued_time ?? null}
                description={currentItem.description}
                pdf={currentItem.pdf as IPdfFile | Record<string, never>}
                evidences={
                  currentItem.evidence_files as { [key: string]: IEvidenceFile }
                }
              />
            )}
          </div>
        </div>
        {/* if only one alert is selected, show the bottom buttons on main, otherwise show the bottom buttons on compare */}
        {desktopBottomButtons()}
      </div>
      {isShareModalOpen && shareModal()}

      {/* Only the main panel renders the prompt — in compare mode both panels
          mount this component, and accessGuard is shared between them.
          Staying here so the bulletin can be shared is the emphasised choice;
          the secondary one lets the navigation through. */}
      <ConfirmDialog
        open={accessGuard.isBlocked && display_type === "main"}
        container={containerRef.current}
        title={t("give_access_title")}
        message={t("give_access_body")}
        primaryLabel={t("yes_give_access")}
        onPrimary={accessGuard.reset}
        secondaryLabel={t("no_continue")}
        onSecondary={accessGuard.proceed}
      />
    </>
  );
};
